import { useUser } from 'contexts/UserContext';
import {
    clearWorkbenchStateBrowserStorage,
    getWorkbenchStateValues,
} from 'pages/workbench/shared';
import * as React from 'react';
import { ReactChild, useCallback, useMemo } from 'react';
import {
    createZustandContext,
    getZustandStoreHook,
    getZustandStoreShallowHook,
    useZustandStore,
    wrapWithZustandContext,
    ZustandContextProvider,
} from 'shared/zustand_context/zustand_context';

import { getWorkbenchStateInitializer } from './initializer';
import { WorkbenchState, WorkbenchStateValues } from './types';

/** Don't use this directly outside of src/pages/workbench/state/* */
export const WorkbenchContext = createZustandContext<WorkbenchState>();

const useWorkbenchStore = getZustandStoreHook(WorkbenchContext);

/**
 * Used to lookup a single value from the workbench state.
 * @example
 * const editMode = useWorkbenchValue((state) => state.editMode);
 */
export const useWorkbenchValue = <TValue,>(value: (state: WorkbenchState) => TValue) =>
    useWorkbenchStore(value);

/**
 * Used to lookup multiple values from the workbench state.
 * @example
 * const { showMinMax, editMode } = useWorkbenchValues(({ tree, trend }) => ({
 *     showMinMax: tree.showMinMax, editMode: trend.editMode
 * }));
 */
export const useWorkbenchValues = getZustandStoreShallowHook(WorkbenchContext);

export const useResetWorkbenchStorage = () => {
    const email = useUser().user?.Email;

    return useCallback(() => {
        if (email) {
            clearWorkbenchStateBrowserStorage(email);
        }
    }, [email]);
};

/**
 * Returns a function reading the Workbench's current values, for event
 * handlers that need a snapshot without subscribing to changes.
 */
export const useGetWorkbenchStateValues = (): (() => WorkbenchStateValues) => {
    const store = useZustandStore(WorkbenchContext);
    return () => getWorkbenchStateValues(store.getState());
};

export type WorkbenchProviderProps = Readonly<{
    children?: React.ReactNode;
    overrides?: Partial<WorkbenchStateValues>;
}>;

export function WorkbenchProvider({ children, overrides }: WorkbenchProviderProps) {
    const initializer = useMemo(
        () => getWorkbenchStateInitializer(overrides),
        [overrides],
    );

    return (
        <ZustandContextProvider ctx={WorkbenchContext} initializer={initializer}>
            {children}
        </ZustandContextProvider>
    );
}

export type ChildWorkbenchProviderProps = Readonly<{ children?: ReactChild }>;

export function ChildWorkbenchProvider({ children }: ChildWorkbenchProviderProps) {
    const store = useZustandStore(WorkbenchContext);
    const overrides = useMemo(() => getWorkbenchStateValues(store.getState()), [store]);

    return <WorkbenchProvider overrides={overrides}>{children}</WorkbenchProvider>;
}

export function wrapWithWorkbenchContext<TProps extends object>(
    Component: React.ComponentType<TProps>,
    overrides: Partial<WorkbenchStateValues> = {},
) {
    return wrapWithZustandContext(
        Component,
        WorkbenchContext,
        getWorkbenchStateInitializer(overrides),
    );
}
